<script setup>
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import Logo from "./Logo.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import WhatsAppButton from "./WhatsAppButton.vue";

const { t } = useI18n();

defineProps({
  isMenuOpen: Boolean,
});

defineEmits(["toggle-menu"]);

document.addEventListener(
  "click",
  function (e) {
    if (e.target.closest('[href*="https://wa.me/"],.bg-green-500')) {
      gtag("event", "conversion", {
        send_to: "AW-992377971/5uk1CJqOrqcaEPP4mdkD",
      });
    }
  },
  {
    capture: true,
  }
);
</script>

<template>
  <header
    id="navigation"
    class="fixed top-0 left-0 right-0 z-40 px-6 py-4 bg-white/95 backdrop-blur-sm"
    role="banner"
  >
    <div class="flex items-center justify-between mx-auto max-w-7xl">
      <RouterLink to="/">
        <Logo />
      </RouterLink>

      <div class="flex items-center gap-4">
        <nav class="items-center hidden gap-8 mr-4 md:flex" role="navigation" aria-label="Main navigation">
          <RouterLink
            to="/"
            class="relative px-4 py-2 text-warm-800 transition-all duration-300 ease-out rounded-lg group hover:text-mint-600 hover:bg-mint-50 hover:scale-105 hover:shadow-lg"
            active-class="bg-mint-100 text-mint-700 shadow-md"
            :aria-current="$route.path === '/' ? 'page' : undefined"
          >
            <span class="relative z-10">{{ t("header.nav.home") }}</span>
            <!-- Subtle background animation -->
            <div class="absolute inset-0 bg-gradient-to-r from-mint-100 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </RouterLink>
          <RouterLink
            to="/services"
            class="relative px-4 py-2 text-warm-800 transition-all duration-300 ease-out rounded-lg group hover:text-mint-600 hover:bg-mint-50 hover:scale-105 hover:shadow-lg"
            active-class="bg-mint-100 text-mint-700 shadow-md"
            :aria-current="$route.path === '/services' ? 'page' : undefined"
          >
            <span class="relative z-10">{{ t("header.nav.services") }}</span>
            <div class="absolute inset-0 bg-gradient-to-r from-mint-100 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </RouterLink>
          <RouterLink
            to="/panel"
            class="relative px-4 py-2 text-warm-800 transition-all duration-300 ease-out rounded-lg group hover:text-mint-600 hover:bg-mint-50 hover:scale-105 hover:shadow-lg"
            active-class="bg-mint-100 text-mint-700 shadow-md"
            :aria-current="$route.path === '/panel' ? 'page' : undefined"
          >
            <span class="relative z-10">{{ t("header.nav.insurance") }}</span>
            <div class="absolute inset-0 bg-gradient-to-r from-mint-100 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </RouterLink>
          <RouterLink
            to="/about"
            class="relative px-4 py-2 text-warm-800 transition-all duration-300 ease-out rounded-lg group hover:text-mint-600 hover:bg-mint-50 hover:scale-105 hover:shadow-lg"
            active-class="bg-mint-100 text-mint-700 shadow-md"
            :aria-current="$route.path === '/about' ? 'page' : undefined"
          >
            <span class="relative z-10">{{ t("header.nav.about") }}</span>
            <div class="absolute inset-0 bg-gradient-to-r from-mint-100 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </RouterLink>
        </nav>

        <LanguageSwitcher />
        <button
          @click="$emit('toggle-menu')"
          class="relative px-6 py-3 text-base transition-all duration-300 ease-out border rounded-full md:hidden hover:bg-mint-600 hover:text-white hover:scale-105 hover:shadow-lg active:scale-95 touch-manipulation"
          :aria-expanded="isMenuOpen"
          aria-label="Toggle mobile navigation menu"
          :aria-controls="isMenuOpen ? 'mobile-menu' : undefined"
        >
          <span class="relative z-10">{{ t("header.menu") }}</span>
          <!-- Ripple effect background -->
          <div class="absolute inset-0 bg-mint-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-0 group-hover:scale-100"></div>
        </button>

        <WhatsAppButton class="hidden md:flex" />
      </div>
    </div>
  </header>
</template>
